// Colours and time formatting shared by the virtual-battery panels and charts.
//
// Concrete hex values rather than SCSS tokens: CanvasJS paints onto a canvas and cannot read
// a CSS variable, and the calendar cells set their fill inline for the same reason.

// The four products a commitment can be sold as. Cyan for FCR-D up (shared by the
// up-and-down product), blue for down, grey-teal for FCR-N, amber for intraday.
export const PRODUCT_COLOR = {
    fcrN: '#6b8f97',
    fcrDUp: '#0e9aa7',
    fcrDDown: '#2c5cff',
    intraday: '#d97706',
};

// The delivery chart's measurement series: magenta / violet / yellow keep clear of the
// product cyan/blue and the responded/missed green/red. `activated` is the wash behind the
// periods the FCR service was activated in.
export const DELIVERY_SERIES_COLOR = {
    availMean: '#b5179e',
    band: '#7b2cbf',
    score: '#c9a227',
    activated: '#7b2cbf',
    endurance: '#0e7490',
    responded: '#448361',
    missed: '#d44c47',
    standby: '#3f8f8c',
};

// The non-measured states' grey — one grey, several alphas, so a withdrawn period looks the
// same on the chart, the calendar, the service strip and the legend.
export const STATE_GREY = '#7e8389';
export const STATE_GREY_FILL = 'rgba(126,131,137,0.22)';
export const STATE_GREY_HATCH = 'rgba(126,131,137,0.45)';
export const STATE_GREY_SWATCH_OOS = 'rgba(126,131,137,0.9)';
export const STATE_GREY_SWATCH_UNMEASURED = 'rgba(126,131,137,0.5)';

/** `#rrggbb` → `rgba(r,g,b,alpha)` — for the calendar cells and the chart washes. */
export const withAlpha = (hex, alpha) => {
    const n = parseInt(hex.slice(1), 16);
    return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `HH:mm` in UTC for a millisecond timestamp. */
export const fmtClock = ms => {
    const d = new Date(ms);
    return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
};

/** `MMM D` (UTC) for a millisecond timestamp. */
export const fmtDay = ms => {
    const d = new Date(ms);
    return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
};

/** `D MMM YYYY` (UTC) — for prose such as the record-start note. */
export const fmtDate = ms => {
    const d = new Date(ms);
    return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

/** `MMM D HH:mm UTC` — the stamp every time-series tooltip labels a point with. */
export const fmtStamp = ms => `${fmtDay(ms)} ${fmtClock(ms)} UTC`;

/** Compact age — "2 d 8 h" / "45 min" — for how stale the newest report is. */
export const fmtAge = ms => {
    const mins = Math.max(0, Math.round(ms / 60000));
    if (mins < 60) {
        return `${mins} min`;
    }
    const hours = Math.floor(mins / 60);
    if (hours < 24) {
        return `${hours} h`;
    }
    return `${Math.floor(hours / 24)} d ${hours % 24} h`;
};

/**
 * Midnight-UTC tick positions across `[from, to]`, thinned so at most `max` labels are
 * emitted.
 */
export const dayTicks = (from, to, max = 8) => {
    const DAY = 86400000;
    const first = Math.ceil(from / DAY) * DAY;
    const all = [];
    for (let t = first; t <= to; t += DAY) {
        all.push(t);
    }
    const step = Math.ceil(all.length / max);
    return step <= 1 ? all : all.filter((_, i) => i % step === 0);
};

/** Tick positions every `everyHours` hours on the UTC grid across `[from, to]`. */
export const hourTicks = (from, to, everyHours) => {
    const STEP = everyHours * 3600000;
    const first = Math.ceil(from / STEP) * STEP;
    const all = [];
    for (let t = first; t <= to; t += STEP) {
        all.push(t);
    }
    return all;
};

/**
 * A millisecond timestamp as the `Date` CanvasJS should be handed so that its **local-time**
 * axis labels and tooltips read as UTC. CanvasJS formats a dateTime axis in the browser's
 * zone with no way to ask for UTC, and settlement periods are UTC-aligned, so every x value
 * (and every strip line bound) is shifted by the local offset before it reaches the chart.
 * Anything printed from the true instant — the tooltip stamps — keeps the unshifted ms on
 * the data point instead.
 */
export const toChartDate = ms => new Date(ms + new Date(ms).getTimezoneOffset() * 60000);

const DAY_AXIS_FROM_MS = 2 * 86400000;
const HOUR_AXIS_TO_MS = 4 * 86400000;

/**
 * CanvasJS `axisX` settings for a time series drawn across the **requested** window.
 *
 * The bounds are always `[fromMs, toMs]`, never the data extent: fitting the axis to the data
 * is what turns one hour of reports inside a seven-day selection into a full, confident
 * week-wide chart. Short windows tick by clock, a few days every 6 h, longer ones by day.
 */
export const timeAxis = (fromMs, toMs) => {
    const span = toMs - fromMs;
    const base = { minimum: toChartDate(fromMs), maximum: toChartDate(toMs) };
    if (span > DAY_AXIS_FROM_MS && span <= HOUR_AXIS_TO_MS) {
        return { ...base, interval: 6, intervalType: 'hour', valueFormatString: 'MMM DD HH:mm' };
    }
    if (span > HOUR_AXIS_TO_MS) {
        return { ...base, interval: Math.max(1, Math.round(span / 86400000 / 8)), intervalType: 'day', valueFormatString: 'MMM DD' };
    }
    return { ...base, interval: span > 6 * 3600000 ? 3 : 1, intervalType: 'hour', valueFormatString: 'HH:mm' };
};
